// frontend/src/pages/Reports.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import {
  readAuditSnapshotIndex,
  getAuditSnapshotPayload,
  rebuildAuditSnapshotIndexFromPayloads,
} from "../lib/auditHistory";
import { getCachedFxRates, convertMoney, guessCurrencySymbol } from "../lib/fxRates";
import { ChevronDown, ChevronUp, Info, RefreshCcw, Wrench } from "lucide-react";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function fmtPct(x) {
  const v = Number(x);
  if (!Number.isFinite(v)) return "—";
  return `${Math.round(v)}%`;
}

function fmtMoney(x, sym) {
  const v = Number(x);
  if (!Number.isFinite(v)) return "—";
  return `${sym || "$"}${Math.round(v).toLocaleString("en-US")}`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function pickCounts(payload) {
  const a = payload?.assessment || {};
  return {
    failed:
      typeof a?.failed_count === "number"
        ? a.failed_count
        : Array.isArray(a?.failed_question_ids)
        ? a.failed_question_ids.length
        : 0,
    unsure:
      typeof a?.unsure_count === "number"
        ? a.unsure_count
        : Array.isArray(a?.unsure_question_ids)
        ? a.unsure_question_ids.length
        : 0,
    total:
      typeof a?.total_questions === "number"
        ? a.total_questions
        : Array.isArray(a?.active_question_ids)
        ? a.active_question_ids.length
        : 60,
  };
}

// Storage keys
const PRIMARY_CCY_KEY = "aifine_reports_primary_currency_v1";
const ACTIVE_BIZ_KEY = "aifine_active_business_id_v1";

// IMPORTANT: some implementations may have business-scoped variants.
// We listen for any key starting with this prefix.
const SNAPSHOT_INDEX_KEY_PREFIX = "aifine_audit_snapshot_index";

// NEW: remediation launch context (snapshot-tied)
const REMEDIATION_CONTEXT_KEY = "aifine_remediation_context_v1";

// NEW: FX display mode (snapshot vs live) — UI-only, snapshot remains immutable
const FX_MODE_KEY = "aifine_reports_fx_mode_v1"; // "SNAPSHOT" | "LIVE"

function safeGetActiveBusinessId() {
  try {
    const v = localStorage.getItem(ACTIVE_BIZ_KEY);
    return v ? String(v) : "";
  } catch {
    return "";
  }
}

function safeReadIndex({ businessId } = {}) {
  try {
    // If businessId is falsy/null/empty, treat as "all" in most implementations.
    const arr = readAuditSnapshotIndex({ businessId: businessId || null });
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function safeGetSnapshotPayload(id) {
  try {
    return getAuditSnapshotPayload(id);
  } catch {
    return null;
  }
}

function snapshotHasExposure(payload) {
  return Boolean(payload?.risk_exposure && typeof payload.risk_exposure === "object");
}

function safeWriteRemediationContext(ctx) {
  try {
    localStorage.setItem(REMEDIATION_CONTEXT_KEY, JSON.stringify(ctx));
  } catch {
    // non-fatal
  }
}

function buildRemediationContext({ snapshotId, payload, meta }) {
  const sid = snapshotId == null ? "" : String(snapshotId);
  const p = payload || null;
  const m = meta || null;

  const businessId =
    p?.business_id || p?.config?.business_id || p?.meta?.business_id || m?.business_id || "";

  const entityName = p?.entity_name || m?.entity_name || "";
  const createdAt =
    p?.created_at_iso || m?.created_at_iso || p?.generated_at || p?.meta?.created_at_iso || "";

  return {
    snapshot_id: sid,
    business_id: businessId ? String(businessId) : "",
    entity_name: entityName ? String(entityName) : "",
    created_at_iso: createdAt ? String(createdAt) : "",
    source: "reports",
    written_at_iso: new Date().toISOString(),
  };
}

/**
 * Normalize exposure payload shape so Reports math doesn't drift.
 * Key fix for incorrect numbers:
 * - If headline exists but combined_by_currency is missing/empty, synthesize a bucket from headline.
 */
function normalizeExposure(exposure) {
  if (!exposure || typeof exposure !== "object") return null;

  const rx = exposure;
  const headline = rx?.headline && typeof rx.headline === "object" ? rx.headline : null;

  const rawBuckets =
    rx?.combined_by_currency && typeof rx.combined_by_currency === "object" ? rx.combined_by_currency : null;

  const bucketKeys = rawBuckets ? Object.keys(rawBuckets).filter(Boolean) : [];
  const hasBuckets = bucketKeys.length > 0;

  // If buckets exist, keep as-is.
  if (hasBuckets) return rx;

  // If no buckets but headline exists, synthesize a single-bucket map.
  const code = headline?.currency_code ? String(headline.currency_code) : "";
  if (!code) return rx;

  const synthesized = {
    combined_structured_worst_case_native: headline?.combined_structured_worst_case_native ?? null,
    combined_net_native: headline?.combined_net_native ?? headline?.combined_net_planning_case_native ?? null,
    combined_floor_native: headline?.combined_floor_native ?? null,
    currency_code: code,
    currency_symbol: headline?.currency_symbol ?? null,
    // keep room for future fields
  };

  return {
    ...rx,
    combined_by_currency: { [code]: synthesized },
  };
}

/**
 * Normalize FX objects into the cache shape convertMoney() expects:
 * { base, rates, source_label, as_of_iso, retrieved_at_iso, version }
 *
 * Supports:
 * - getCachedFxRates() cache shape
 * - getFxRatesPlanning() planning shape stored in snapshot payload.fx
 */
function normalizeFxCache(anyFx) {
  if (!anyFx || typeof anyFx !== "object") return null;

  // getCachedFxRates shape
  if (anyFx?.rates && typeof anyFx.rates === "object" && (anyFx?.base || anyFx?.source_label)) {
    const base = String(anyFx.base || "EUR").toUpperCase();
    const ratesRaw = anyFx.rates || {};
    const rates = {};
    Object.keys(ratesRaw).forEach((k) => {
      const c = String(k || "").trim().toUpperCase();
      const v = Number(ratesRaw[k]);
      if (c && Number.isFinite(v) && v > 0) rates[c] = v;
    });

    if (!Object.keys(rates).length) return null;

    return {
      version: anyFx?.version || anyFx?.cache_version || "fx.normalized",
      source_label: anyFx?.source_label || "FX source",
      retrieved_at_iso: anyFx?.retrieved_at_iso || null,
      as_of_iso: anyFx?.as_of_iso || null,
      base,
      rates,
    };
  }

  // getFxRatesPlanning shape (snapshot payload.fx)
  if (anyFx?.rates && typeof anyFx.rates === "object" && anyFx?.base) {
    const base = String(anyFx.base || "EUR").toUpperCase();
    const ratesRaw = anyFx.rates || {};
    const rates = {};
    Object.keys(ratesRaw).forEach((k) => {
      const c = String(k || "").trim().toUpperCase();
      const v = Number(ratesRaw[k]);
      if (c && Number.isFinite(v) && v > 0) rates[c] = v;
    });

    if (!Object.keys(rates).length) return null;

    return {
      version: anyFx?.cache_version || anyFx?.version || "fx.planning",
      source_label: anyFx?.source_label || "FX source",
      retrieved_at_iso: anyFx?.retrieved_at_iso || anyFx?.retrievedAt || null,
      as_of_iso: anyFx?.as_of_iso || null,
      base,
      rates,
    };
  }

  return null;
}

function readFxModePref() {
  try {
    const v = localStorage.getItem(FX_MODE_KEY);
    const s = String(v || "").toUpperCase();
    if (s === "LIVE" || s === "SNAPSHOT") return s;
    return "SNAPSHOT";
  } catch {
    return "SNAPSHOT";
  }
}

function writeFxModePref(mode) {
  try {
    const s = String(mode || "SNAPSHOT").toUpperCase();
    localStorage.setItem(FX_MODE_KEY, s === "LIVE" ? "LIVE" : "SNAPSHOT");
  } catch {
    // ignore
  }
}

export default function Reports() {
  const navigate = useNavigate();

  // Active business (drives which snapshots are shown)
  const [activeBizId, setActiveBizId] = useState(() => safeGetActiveBusinessId());

  // UI toggle: show only active business vs show all snapshots (debug)
  const [showAllSnapshots, setShowAllSnapshots] = useState(false);

  // Snapshot index (filtered or unfiltered)
  const [index, setIndex] = useState(() => {
    const bid = safeGetActiveBusinessId();
    const raw = safeReadIndex({ businessId: bid });
    return raw;
  });

  // Selected snapshot id
  const [selectedId, setSelectedId] = useState(() => {
    const bid = safeGetActiveBusinessId();
    const raw = safeReadIndex({ businessId: bid });
    return raw?.[0]?.snapshot_id ?? null;
  });

  // Primary currency selection (persisted)
  const [primaryCurrency, setPrimaryCurrency] = useState(() => {
    try {
      return localStorage.getItem(PRIMARY_CCY_KEY) || "";
    } catch {
      return "";
    }
  });

  // FX mode (snapshot default; live is optional)
  const [fxMode, setFxMode] = useState(() => readFxModePref());

  // Live FX cache state (Layer 2)
  const [fxLive, setFxLive] = useState(() => ({
    status: "idle", // idle | loading | ready | error
    cache: null,
    error: null,
    last_refreshed_iso: null,
  }));

  // Explanations dropdown
  const [showExplain, setShowExplain] = useState(false);

  // Quick action status banner
  const [statusNote, setStatusNote] = useState("");

  const explainText = {
    worst:
      "Structured Worst Case is the model’s conservative headline estimate for your selected snapshot, bounded by statutory ceilings. It is a planning number, not legal advice.",
    net:
      "Net Planning Case is a discounted planning estimate that assumes practical reducing factors often present in enforcement (e.g., scope narrowing, remediation, cooperation, governance maturity, counsel strategy).",
    floor:
      "Planning Floor is a lower planning anchor used for budget and prioritization. Real outcomes can be above or below this depending on facts, enforcement posture, and jurisdiction.",
    overlay:
      "If you see “Inferred Federal Overlay”, it means the system inferred potential federal enforcement surface based on your sector/activity signals. Verify applicability with counsel.",
    fx:
      "Multi-currency reports store native totals per currency. Converted totals (when shown) are display-only planning conversions into your selected primary currency. Snapshots remain immutable.",
  };

  const refresh = (opts = {}) => {
    const { keepSelection = true } = opts;

    const nextActive = safeGetActiveBusinessId();
    setActiveBizId(nextActive);

    const nextIndex = showAllSnapshots
      ? safeReadIndex({ businessId: null }) // unfiltered
      : safeReadIndex({ businessId: nextActive }); // filtered

    setIndex(nextIndex);

    setSelectedId((prev) => {
      const prevKey = prev == null ? null : String(prev);

      if (keepSelection && prevKey && nextIndex.some((r) => String(r?.snapshot_id) === prevKey)) return prev;

      // Prefer the first snapshot that actually has exposure to avoid misleading default.
      for (const row of nextIndex) {
        const sid = row?.snapshot_id;
        if (!sid) continue;
        const p = safeGetSnapshotPayload(sid);
        if (snapshotHasExposure(p)) return sid;
      }

      // Fallback to newest snapshot id
      return nextIndex?.[0]?.snapshot_id ?? null;
    });
  };

  // Refresh pipeline: on mount, focus, storage changes, showAllSnapshots toggle
  useEffect(() => {
    refresh({ keepSelection: true });

    const onFocus = () => refresh({ keepSelection: true });

    const onStorage = (e) => {
      if (!e || e.key == null) return;
      const k = String(e.key);

      // Active business changed
      if (k === ACTIVE_BIZ_KEY) {
        refresh({ keepSelection: true });
        return;
      }

      // Snapshot index changed (support prefix / business-scoped variants)
      if (k.startsWith(SNAPSHOT_INDEX_KEY_PREFIX)) {
        refresh({ keepSelection: true });
        return;
      }
    };

    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllSnapshots]);

  // Persist primary currency selection
  useEffect(() => {
    try {
      if (primaryCurrency) localStorage.setItem(PRIMARY_CCY_KEY, primaryCurrency);
    } catch {
      // ignore
    }
  }, [primaryCurrency]);

  // Persist fx mode selection
  useEffect(() => {
    writeFxModePref(fxMode);
  }, [fxMode]);

  const selectedPayload = useMemo(() => {
    if (!selectedId) return null;
    return safeGetSnapshotPayload(selectedId);
  }, [selectedId]);

  const selectedMeta = useMemo(() => {
    if (!selectedId) return null;
    return (index || []).find((r) => String(r?.snapshot_id) === String(selectedId)) || null;
  }, [index, selectedId]);

  const entityName = selectedPayload?.entity_name || selectedMeta?.entity_name || "Not specified";
  const scopeArr = selectedPayload?.summary?.jurisdictions || selectedMeta?.scope || [];
  const scopeText = Array.isArray(scopeArr) && scopeArr.length ? scopeArr.join(", ") : "—";

  const assessment = selectedPayload?.assessment || {};
  const scorePct = assessment?.compliance_score_pct;

  const counts = pickCounts(selectedPayload);
  const passed = Math.max(0, Number(counts.total || 0) - Number(counts.failed || 0) - Number(counts.unsure || 0));

  // STRICT snapshot-driven exposure (normalized)
  const exposureRaw = selectedPayload?.risk_exposure || null;
  const exposure = useMemo(() => normalizeExposure(exposureRaw), [exposureRaw]);

  const perRegime = exposure?.per_regime || {};
  const combinedByCurrency = exposure?.combined_by_currency || null;

  // Determine available currency buckets (robust)
  const engineHeadline = exposure?.headline || null;
  const engineHeadlineCode = engineHeadline?.currency_code ? String(engineHeadline.currency_code) : "";

  const currencyCodes = useMemo(() => {
    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    const keys = Object.keys(m || {}).filter(Boolean);
    if (keys.length) return keys;
    if (engineHeadlineCode) return [engineHeadlineCode];
    return [];
  }, [combinedByCurrency, engineHeadlineCode]);

  const isMultiCurrency = currencyCodes.length > 1;

  // Snapshot FX (Layer 1) — comes from selected payload only (immutable)
  const fxSnapshotRaw = selectedPayload?.fx || null;
  const fxSnapshot = useMemo(() => normalizeFxCache(fxSnapshotRaw), [fxSnapshotRaw]);
  const hasSnapshotFx = Boolean(fxSnapshot && fxSnapshot.rates && Object.keys(fxSnapshot.rates).length);

  // Default to SNAPSHOT when available; otherwise LIVE (safety)
  useEffect(() => {
    if (!isMultiCurrency) return;

    // If snapshot FX exists, we force default SNAPSHOT unless user explicitly chose LIVE previously.
    const saved = readFxModePref();
    if (hasSnapshotFx) {
      // keep user's explicit LIVE if previously set; otherwise SNAPSHOT
      if (saved !== "LIVE" && fxMode !== "SNAPSHOT") setFxMode("SNAPSHOT");
      if (saved === "SNAPSHOT" && fxMode !== "SNAPSHOT") setFxMode("SNAPSHOT");
    } else {
      // No snapshot FX → must rely on LIVE for conversion (optional display)
      if (fxMode !== "LIVE") setFxMode("LIVE");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiCurrency, hasSnapshotFx, selectedId]);

  // Choose primary currency
  const chosenPrimaryCode = useMemo(() => {
    if (primaryCurrency && currencyCodes.includes(primaryCurrency)) return primaryCurrency;
    if (engineHeadlineCode && currencyCodes.includes(engineHeadlineCode)) return engineHeadlineCode;
    return currencyCodes[0] || "";
  }, [primaryCurrency, currencyCodes, engineHeadlineCode]);

  const bucketPrimary = useMemo(() => {
    if (!combinedByCurrency || !chosenPrimaryCode) return null;
    const b = combinedByCurrency[chosenPrimaryCode];
    return b && typeof b === "object" ? b : null;
  }, [combinedByCurrency, chosenPrimaryCode]);

  // Secondary buckets (everything not primary)
  const secondaryBuckets = useMemo(() => {
    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    const codes = Object.keys(m || {}).filter(Boolean);
    return codes
      .filter((c) => c !== chosenPrimaryCode)
      .map((code) => ({ code, bucket: m[code] }))
      .filter((x) => x.bucket && typeof x.bucket === "object");
  }, [combinedByCurrency, chosenPrimaryCode]);

  // Live FX loader (Layer 2): only when multi-currency AND user selects LIVE mode
  useEffect(() => {
    const shouldUseLiveFx = isMultiCurrency && Boolean(chosenPrimaryCode) && fxMode === "LIVE";
    if (!shouldUseLiveFx) {
      setFxLive((s) => ({ ...s, status: "idle", error: null }));
      return;
    }

    let alive = true;
    (async () => {
      try {
        setFxLive({ status: "loading", cache: null, error: null, last_refreshed_iso: null });
        const cache = await getCachedFxRates({ maxAgeHours: 12, forceRefresh: false });
        if (!alive) return;
        setFxLive({ status: "ready", cache, error: null, last_refreshed_iso: null });
      } catch (err) {
        if (!alive) return;
        setFxLive({ status: "error", cache: null, error: String(err?.message || err || "FX error"), last_refreshed_iso: null });
      }
    })();

    return () => {
      alive = false;
    };
  }, [isMultiCurrency, chosenPrimaryCode, fxMode]);

  const handleRefreshLiveFx = async () => {
    if (!isMultiCurrency) return;
    if (!chosenPrimaryCode) return;

    try {
      setFxMode("LIVE"); // user intent is live conversion
      setFxLive((s) => ({ ...s, status: "loading", error: null }));
      const cache = await getCachedFxRates({ maxAgeHours: 12, forceRefresh: true });
      setFxLive({ status: "ready", cache, error: null, last_refreshed_iso: new Date().toISOString() });
      setStatusNote("FX refreshed (display-only conversion updated).");
      window.setTimeout(() => setStatusNote(""), 1800);
    } catch (err) {
      setFxLive({ status: "error", cache: null, error: String(err?.message || err || "FX refresh error"), last_refreshed_iso: null });
      setStatusNote("FX refresh failed (showing audit-date/native totals).");
      window.setTimeout(() => setStatusNote(""), 2400);
    }
  };

  // Pick which FX cache powers *converted* headline totals (Layer 1 vs Layer 2)
  const fxForConversion = useMemo(() => {
    if (!isMultiCurrency) return null;
    if (!chosenPrimaryCode) return null;

    if (fxMode === "SNAPSHOT") return hasSnapshotFx ? fxSnapshot : null;

    // LIVE
    const liveNorm = normalizeFxCache(fxLive?.cache || null);
    return liveNorm && liveNorm.rates ? liveNorm : null;
  }, [isMultiCurrency, chosenPrimaryCode, fxMode, hasSnapshotFx, fxSnapshot, fxLive?.cache]);

  const fxEnabled = isMultiCurrency && Boolean(fxForConversion && fxForConversion.rates && chosenPrimaryCode);

  // FX hybrid headline totals (display only)
  const headlineConverted = useMemo(() => {
    const out = { ok: false, worst: null, net: null, floor: null, missing: [] };

    if (!combinedByCurrency || !chosenPrimaryCode) return out;
    if (!fxEnabled) return out;

    const cache = fxForConversion;

    let worst = 0;
    let net = 0;
    let floor = 0;

    const missing = new Set();

    Object.keys(combinedByCurrency || {}).forEach((code) => {
      const b = combinedByCurrency[code];
      if (!b) return;

      const w = Number(b?.combined_structured_worst_case_native);
      const n2 = Number(b?.combined_net_native);
      const f2 = Number(b?.combined_floor_native);

      const cw = Number.isFinite(w) ? convertMoney(w, code, chosenPrimaryCode, cache) : null;
      const cn2 = Number.isFinite(n2) ? convertMoney(n2, code, chosenPrimaryCode, cache) : null;
      const cf2 = Number.isFinite(f2) ? convertMoney(f2, code, chosenPrimaryCode, cache) : null;

      if (Number.isFinite(w) && cw == null) missing.add(String(code));
      if (Number.isFinite(n2) && cn2 == null) missing.add(String(code));
      if (Number.isFinite(f2) && cf2 == null) missing.add(String(code));

      if (typeof cw === "number") worst += cw;
      if (typeof cn2 === "number") net += cn2;
      if (typeof cf2 === "number") floor += cf2;
    });

    out.ok = true;
    out.worst = Math.round(worst);
    out.net = Math.round(net);
    out.floor = Math.round(floor);
    out.missing = Array.from(missing);
    return out;
  }, [combinedByCurrency, chosenPrimaryCode, fxEnabled, fxForConversion]);

  const primarySymbol = useMemo(() => {
    const sym =
      bucketPrimary?.currency_symbol ||
      engineHeadline?.currency_symbol ||
      selectedPayload?.summary?.currency_symbol ||
      null;
    return sym || guessCurrencySymbol(chosenPrimaryCode);
  }, [bucketPrimary, engineHeadline, selectedPayload, chosenPrimaryCode]);

  const headlineWorstFallback = engineHeadline?.combined_structured_worst_case_native ?? null;
  const headlineNetFallback =
    engineHeadline?.combined_net_native ?? engineHeadline?.combined_net_planning_case_native ?? null;
  const headlineFloorFallback = engineHeadline?.combined_floor_native ?? null;

  const showHeadlineFx = fxEnabled && headlineConverted.ok && typeof headlineConverted.worst === "number";

  const hasSnapshots = Array.isArray(index) && index.length > 0;

  const doRebuildIndex = () => {
    try {
      setStatusNote("Rebuilding snapshot index from stored payloads…");
      rebuildAuditSnapshotIndexFromPayloads();
      setStatusNote("Snapshot index rebuilt. Refreshing…");
      refresh({ keepSelection: false });
      window.setTimeout(() => setStatusNote(""), 1800);
    } catch (err) {
      setStatusNote(`Rebuild failed: ${String(err?.message || err || "Unknown error")}`);
      window.setTimeout(() => setStatusNote(""), 3500);
    }
  };

  const selectedCreatedAt =
    selectedPayload?.created_at_iso ||
    selectedMeta?.created_at_iso ||
    selectedPayload?.generated_at ||
    selectedPayload?.meta?.created_at_iso ||
    null;

  const selectedBusinessId =
    selectedPayload?.business_id ||
    selectedPayload?.config?.business_id ||
    selectedPayload?.meta?.business_id ||
    selectedMeta?.business_id ||
    "—";

  function goToRemediationForSnapshot({ snapshotId, payload, meta }) {
    const sid = snapshotId == null ? "" : String(snapshotId);
    if (!sid) {
      setStatusNote("No snapshot selected. Select a snapshot first.");
      window.setTimeout(() => setStatusNote(""), 2200);
      return;
    }
    const ctx = buildRemediationContext({ snapshotId: sid, payload, meta });
    safeWriteRemediationContext(ctx);
    navigate("/remediation");
  }

  const fxDisclosure = useMemo(() => {
    if (!isMultiCurrency) return null;

    const auditFx = hasSnapshotFx ? fxSnapshot : null;
    const liveFx = normalizeFxCache(fxLive?.cache || null);

    const modeLabel =
      fxMode === "SNAPSHOT"
        ? "Audit-date FX (from saved snapshot)"
        : "Current FX (refreshed; display-only)";

    const activeFx =
      fxMode === "SNAPSHOT" ? auditFx : liveFx;

    const activeAsOf = activeFx?.as_of_iso || null;
    const activeSource = activeFx?.source_label || null;

    const auditAsOf = auditFx?.as_of_iso || null;
    const auditSource = auditFx?.source_label || null;

    return {
      modeLabel,
      activeAsOf,
      activeSource,
      auditAsOf,
      auditSource,
      liveAsOf: liveFx?.as_of_iso || null,
      liveSource: liveFx?.source_label || null,
    };
  }, [isMultiCurrency, fxMode, hasSnapshotFx, fxSnapshot, fxLive?.cache]);

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-auto p-8 md:p-12">
        <div className="mx-auto w-full max-w-6xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-3xl font-extrabold tracking-tight">Reports</div>
              <div className="mt-1 text-sm text-slate-600">
                Executive summary and key findings from your Assessment Audits.
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full border bg-white px-3 py-1">
                  Active Business ID: <span className="font-bold">{activeBizId || "—"}</span>
                </span>
                <span className="rounded-full border bg-white px-3 py-1">
                  Legal Entity: <span className="font-bold">{entityName}</span>
                </span>
                <span className="rounded-full border bg-white px-3 py-1">
                  Scope: <span className="font-bold">{scopeText}</span>
                </span>
                <span className="rounded-full border bg-white px-3 py-1">
                  Mode: <span className="font-bold">Executive</span>
                </span>
              </div>

              {/* Selected snapshot metadata */}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
                <span className="rounded-full border bg-white px-3 py-1">
                  Selected snapshot:{" "}
                  <span className="font-mono font-black">{selectedId ? String(selectedId) : "—"}</span>
                </span>
                <span className="rounded-full border bg-white px-3 py-1">
                  Snapshot business_id: <span className="font-black">{String(selectedBusinessId || "—")}</span>
                </span>
                <span className="rounded-full border bg-white px-3 py-1">
                  Created: <span className="font-black">{fmtDateTime(selectedCreatedAt)}</span>
                </span>

                <button
                  type="button"
                  onClick={() => setShowAllSnapshots((v) => !v)}
                  className={cn(
                    "rounded-full border px-3 py-1 font-black",
                    showAllSnapshots ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:bg-slate-50"
                  )}
                  title="If snapshots appear to disappear, toggle to show all snapshots (across businesses)."
                >
                  {showAllSnapshots ? "Showing: ALL snapshots" : "Showing: Active business only"}
                </button>
              </div>

              {statusNote ? (
                <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs text-slate-700">
                  <span className="font-black">Status:</span> {statusNote}
                </div>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/dashboard")}
                className="rounded-2xl border bg-white px-4 py-2 text-sm font-bold hover:border-slate-300"
              >
                Back to Dashboard →
              </button>

              {/* UPDATED: snapshot-tied handoff */}
              <button
                onClick={() =>
                  goToRemediationForSnapshot({
                    snapshotId: selectedId,
                    payload: selectedPayload,
                    meta: selectedMeta,
                  })
                }
                className="rounded-2xl border bg-white px-4 py-2 text-sm font-bold hover:border-slate-300"
                title="Go to Remediation for the currently selected snapshot"
              >
                Go to Remediation
              </button>

              <button
                type="button"
                onClick={() => refresh({ keepSelection: true })}
                className="rounded-2xl border bg-white px-3 py-2 text-sm font-black hover:bg-slate-50 inline-flex items-center gap-2"
                title="Refresh snapshot list and selection"
              >
                <RefreshCcw size={16} />
                Refresh
              </button>
            </div>
          </div>

          {/* Empty state */}
          {!hasSnapshots ? (
            <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-lg font-black">No stored assessment snapshots in this view.</div>

              <div className="mt-2 text-sm text-slate-600">
                This page normally filters snapshots by <span className="font-black">{ACTIVE_BIZ_KEY}</span>.
                If you ran an audit under a different business, toggle{" "}
                <span className="font-black">“Showing: ALL snapshots”</span> to confirm the snapshots exist, then
                switch the active business on Dashboard.
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  onClick={() => navigate("/assessment")}
                  className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-black text-white hover:bg-slate-800"
                >
                  Go to Assessment →
                </button>

                <button
                  onClick={() => navigate("/dashboard")}
                  className="rounded-2xl border bg-white px-4 py-2 text-sm font-bold hover:border-slate-300"
                >
                  Back to Dashboard
                </button>

                <button
                  onClick={doRebuildIndex}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black hover:bg-slate-50 inline-flex items-center gap-2"
                  title="If index is missing/corrupted but payloads exist, rebuild the index from payload keys."
                >
                  <Wrench size={16} />
                  Rebuild snapshot index
                </button>

                <button
                  onClick={() => setShowAllSnapshots(true)}
                  className="rounded-2xl border bg-white px-4 py-2 text-sm font-black hover:bg-slate-50"
                >
                  Show all snapshots (debug)
                </button>
              </div>

              <div className="mt-10 pb-2 text-xs text-slate-400">
                © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
              </div>
            </div>
          ) : (
            <>
              {/* Exposure */}
              <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                      Combined Stacked Risk Exposure (multi-regime)
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      Based on the selected stored snapshot below. (Snapshot ID:{" "}
                      <span className="font-mono font-black">{selectedId ? String(selectedId) : "—"}</span>)
                    </div>

                    {/* Currency + FX controls (multi-currency only) */}
                    {isMultiCurrency ? (
                      <div className="mt-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <span className="rounded-full border bg-white px-3 py-1">
                            Primary currency: <span className="font-black">{chosenPrimaryCode}</span>
                          </span>

                          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-900 font-black">
                            Multi-currency
                          </span>

                          <div className="ml-0 md:ml-2 inline-flex items-center gap-2">
                            <span className="text-slate-600 font-bold">Choose primary currency:</span>
                            <select
                              value={
                                primaryCurrency && currencyCodes.includes(primaryCurrency)
                                  ? primaryCurrency
                                  : chosenPrimaryCode
                              }
                              onChange={(e) => setPrimaryCurrency(String(e.target.value || ""))}
                              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black"
                            >
                              {currencyCodes.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* FX Mode selector + Refresh */}
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <span className="text-slate-600 font-bold">FX conversion mode:</span>

                          <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1">
                            <button
                              type="button"
                              onClick={() => setFxMode("SNAPSHOT")}
                              className={cn(
                                "rounded-xl px-3 py-1.5 text-[11px] font-black",
                                fxMode === "SNAPSHOT" ? "bg-slate-900 text-white" : "text-slate-700"
                              )}
                              title="Default: use FX captured at audit time (stored in the immutable snapshot)."
                              disabled={!hasSnapshotFx}
                            >
                              Audit-date FX (default)
                            </button>
                            <button
                              type="button"
                              onClick={() => setFxMode("LIVE")}
                              className={cn(
                                "rounded-xl px-3 py-1.5 text-[11px] font-black",
                                fxMode === "LIVE" ? "bg-slate-900 text-white" : "text-slate-700"
                              )}
                              title="Optional: use current FX for display-only conversion. Snapshot remains unchanged."
                            >
                              Current FX (display-only)
                            </button>
                          </div>

                          <button
                            type="button"
                            onClick={handleRefreshLiveFx}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-black hover:bg-slate-50 inline-flex items-center gap-2"
                            title="Fetch current FX rates and update converted totals (does not change snapshot)."
                          >
                            <RefreshCcw size={14} />
                            Refresh FX conversion
                          </button>

                          <span className="text-slate-500">
                            {fxMode === "SNAPSHOT"
                              ? hasSnapshotFx
                                ? "Using audit-date FX stored in snapshot."
                                : "Snapshot FX missing; conversions use native totals only."
                              : fxLive.status === "loading"
                              ? "Loading current FX…"
                              : fxLive.status === "error"
                              ? "Current FX unavailable (showing audit-date/native)."
                              : "Using current FX for display-only conversion."}
                          </span>
                        </div>
                      </div>
                    ) : currencyCodes.length === 1 ? (
                      <div className="mt-3 text-xs text-slate-500">
                        Currency: <span className="font-black">{currencyCodes[0]}</span>
                      </div>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowExplain((v) => !v)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black hover:bg-slate-50"
                    title="Show / hide what these numbers mean"
                  >
                    <Info size={14} />
                    What do these mean?
                    {showExplain ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>

                {/* Missing exposure */}
                {hasSnapshots && !exposure ? (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
                    <div className="font-black">Risk Exposure not available for selected snapshot.</div>
                    <div className="mt-1 text-xs text-amber-950/80">
                      This usually means the snapshot was created before exposure computation was added, or the run was
                      interrupted before exposure could be persisted.
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          for (const row of index || []) {
                            const sid = row?.snapshot_id;
                            if (!sid) continue;
                            const p = safeGetSnapshotPayload(sid);
                            if (snapshotHasExposure(p)) {
                              setSelectedId(sid);
                              setStatusNote("Selected a snapshot with exposure.");
                              window.setTimeout(() => setStatusNote(""), 1600);
                              return;
                            }
                          }
                          setStatusNote("No snapshots with exposure found in this list.");
                          window.setTimeout(() => setStatusNote(""), 2200);
                        }}
                        className="rounded-2xl bg-slate-900 px-4 py-2 text-xs font-black text-white hover:bg-slate-800"
                      >
                        Select a snapshot with exposure
                      </button>

                      <button
                        type="button"
                        onClick={doRebuildIndex}
                        className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-black hover:bg-slate-50 inline-flex items-center gap-2"
                        title="Rebuild index from payload keys"
                      >
                        <Wrench size={14} />
                        Rebuild snapshot index
                      </button>

                      <button
                        type="button"
                        onClick={() => refresh({ keepSelection: false })}
                        className="rounded-2xl border bg-white px-4 py-2 text-xs font-black hover:bg-slate-50"
                      >
                        Refresh list
                      </button>
                    </div>
                  </div>
                ) : !exposure ? (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
                    Risk Exposure not available.
                  </div>
                ) : (
                  <>
                    {/* Headline trio */}
                    <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                        <div className="text-xs font-black uppercase tracking-wide text-slate-500">
                          Structured Worst Case
                        </div>
                        <div className="mt-2 text-3xl font-black text-red-700">
                          {showHeadlineFx
                            ? fmtMoney(headlineConverted.worst, primarySymbol)
                            : bucketPrimary
                            ? fmtMoney(bucketPrimary?.combined_structured_worst_case_native, primarySymbol)
                            : fmtMoney(headlineWorstFallback, primarySymbol)}
                        </div>
                      </div>

                      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                        <div className="text-xs font-black uppercase tracking-wide text-slate-500">
                          Net Planning Case
                        </div>
                        <div className="mt-2 text-3xl font-black text-blue-600">
                          {showHeadlineFx
                            ? fmtMoney(headlineConverted.net, primarySymbol)
                            : bucketPrimary
                            ? fmtMoney(bucketPrimary?.combined_net_native, primarySymbol)
                            : fmtMoney(headlineNetFallback, primarySymbol)}
                        </div>
                      </div>

                      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                        <div className="text-xs font-black uppercase tracking-wide text-slate-500">
                          Planning Floor
                        </div>
                        <div className="mt-2 text-3xl font-black text-blue-600">
                          {showHeadlineFx
                            ? fmtMoney(headlineConverted.floor, primarySymbol)
                            : bucketPrimary
                            ? fmtMoney(bucketPrimary?.combined_floor_native, primarySymbol)
                            : fmtMoney(headlineFloorFallback, primarySymbol)}
                        </div>
                      </div>
                    </div>

                    {/* FX disclosure (multi-currency only) */}
                    {isMultiCurrency && fxDisclosure ? (
                      <div className="mt-3 text-[11px] text-slate-500">
                        <span className="font-black text-slate-700">FX disclosure:</span>{" "}
                        <span className="font-black">{fxDisclosure.modeLabel}</span>. Converted headline totals (if shown) convert
                        multi-currency totals into <span className="font-black">{chosenPrimaryCode}</span>. Planning only; real
                        enforcement outcomes vary.
                        <br />
                        <span className="text-slate-600">
                          Audit-date FX source:{" "}
                          <span className="font-black">{fxDisclosure.auditSource || "—"}</span> · as of{" "}
                          <span className="font-black">{fxDisclosure.auditAsOf ? fmtDateTime(fxDisclosure.auditAsOf) : "—"}</span>
                          .
                        </span>
                        {" "}
                        <span className="text-slate-600">
                          Current FX source:{" "}
                          <span className="font-black">{fxDisclosure.liveSource || "—"}</span> · as of{" "}
                          <span className="font-black">{fxDisclosure.liveAsOf ? fmtDateTime(fxDisclosure.liveAsOf) : "—"}</span>
                          .
                        </span>
                        {headlineConverted?.missing?.length ? (
                          <>
                            {" "}
                            <span className="text-amber-700 font-black">Note:</span> Missing FX rates for:{" "}
                            <span className="font-black">{headlineConverted.missing.join(", ")}</span>. Those currencies are
                            excluded from converted headline totals.
                          </>
                        ) : null}
                      </div>
                    ) : null}

                    {/* Explanations dropdown */}
                    {showExplain ? (
                      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-700 space-y-2">
                        <div>
                          <span className="font-black text-slate-900">Structured Worst Case:</span> {explainText.worst}
                        </div>
                        <div>
                          <span className="font-black text-slate-900">Net Planning Case:</span> {explainText.net}
                        </div>
                        <div>
                          <span className="font-black text-slate-900">Planning Floor:</span> {explainText.floor}
                        </div>
                        <div className="text-slate-600">
                          <span className="font-black text-slate-900">Federal/State note:</span> {explainText.overlay}
                        </div>
                        <div className="text-slate-600">
                          <span className="font-black text-slate-900">Currency note:</span> {explainText.fx}
                        </div>
                      </div>
                    ) : null}

                    {/* Secondary currency totals */}
                    {secondaryBuckets.length ? (
                      <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-5">
                        <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                          Secondary Currency Totals
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          Native totals per currency. Converted preview is informational only.
                        </div>

                        <div className="mt-4 space-y-2">
                          {secondaryBuckets.map(({ code, bucket }) => {
                            const sym = bucket?.currency_symbol || guessCurrencySymbol(code) || "$";

                            const w = Number(bucket?.combined_structured_worst_case_native);
                            const n2 = Number(bucket?.combined_net_native);
                            const f2 = Number(bucket?.combined_floor_native);

                            const convW = fxEnabled && Number.isFinite(w) ? convertMoney(w, code, chosenPrimaryCode, fxForConversion) : null;
                            const convN = fxEnabled && Number.isFinite(n2) ? convertMoney(n2, code, chosenPrimaryCode, fxForConversion) : null;
                            const convF = fxEnabled && Number.isFinite(f2) ? convertMoney(f2, code, chosenPrimaryCode, fxForConversion) : null;

                            const hasConv =
                              typeof convW === "number" || typeof convN === "number" || typeof convF === "number";

                            return (
                              <div
                                key={code}
                                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                              >
                                <div className="inline-flex items-center gap-2">
                                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black">
                                    {code}
                                  </span>
                                  <span className="text-xs text-slate-500 font-bold">Native</span>
                                </div>

                                <div className="text-sm md:text-base font-black flex flex-wrap items-center gap-4">
                                  <span className="text-slate-800">
                                    Worst: <span className="text-red-700">{fmtMoney(w, sym)}</span>
                                  </span>
                                  <span className="text-slate-800">
                                    Net: <span className="text-blue-600">{fmtMoney(n2, sym)}</span>
                                  </span>
                                  <span className="text-slate-800">
                                    Floor: <span className="text-blue-600">{fmtMoney(f2, sym)}</span>
                                  </span>
                                </div>

                                {isMultiCurrency ? (
                                  <div className="w-full text-[11px] text-slate-500">
                                    {fxEnabled && hasConv ? (
                                      <>
                                        Converted to <span className="font-black">{chosenPrimaryCode}</span>:{" "}
                                        <span className="text-slate-700 font-black">
                                          Worst {fmtMoney(convW, primarySymbol)}
                                        </span>{" "}
                                        •{" "}
                                        <span className="text-slate-700 font-black">
                                          Net {fmtMoney(convN, primarySymbol)}
                                        </span>{" "}
                                        •{" "}
                                        <span className="text-slate-700 font-black">
                                          Floor {fmtMoney(convF, primarySymbol)}
                                        </span>{" "}
                                        <span className="text-slate-400">(planning only)</span>
                                      </>
                                    ) : fxMode === "LIVE" && fxLive.status === "loading" ? (
                                      <>Converted preview: loading current FX…</>
                                    ) : fxMode === "LIVE" && fxLive.status === "error" ? (
                                      <>Converted preview unavailable (FX error).</>
                                    ) : (
                                      <>Converted preview unavailable.</>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    {/* Per-regime lines */}
                    <div className="mt-5 space-y-2">
                      {Object.keys(perRegime).map((k) => {
                        const e = perRegime[k];
                        const sym = e.currency_symbol || guessCurrencySymbol(e.currency_code) || "$";
                        const isOverlay = Boolean(e?.inferred_overlay);

                        return (
                          <div
                            key={k}
                            className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3"
                          >
                            <div>
                              <div className="text-sm font-black inline-flex items-center gap-2">
                                {e.display_name || k}
                                {isOverlay ? (
                                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-black text-amber-900">
                                    Inferred overlay
                                  </span>
                                ) : null}
                              </div>
                              <div className="text-xs text-slate-500">
                                Ceiling: {fmtMoney(e.statutory_ceiling_native, sym)}
                              </div>
                            </div>

                            <div className="text-sm font-black">{fmtMoney(e.structured_worst_case_native, sym)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Snapshot History */}
              <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                  Assessment Audit Snapshot History (stored)
                </div>

                <div className="mt-4 text-xs text-slate-500">
                  Click a row to update the exposure panel above. Use <span className="font-black">“View →”</span> to open the immutable snapshot.
                  {showAllSnapshots ? (
                    <>
                      {" "}
                      <span className="font-black text-slate-700">Debug mode:</span> showing snapshots across all businesses.
                    </>
                  ) : null}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={doRebuildIndex}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-black hover:bg-slate-50 inline-flex items-center gap-2"
                    title="Rebuild index from payload keys (use if list is empty but payloads exist)."
                  >
                    <Wrench size={14} />
                    Rebuild snapshot index
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowAllSnapshots((v) => !v)}
                    className={cn(
                      "rounded-2xl border px-4 py-2 text-xs font-black hover:bg-slate-50",
                      showAllSnapshots ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-800" : "bg-white"
                    )}
                  >
                    {showAllSnapshots ? "Show active business only" : "Show all snapshots (debug)"}
                  </button>
                </div>

                <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <div className="grid grid-cols-7 px-4 py-3 text-[11px] font-black uppercase tracking-[0.25em] text-slate-500 bg-slate-50">
                    <div className="col-span-2">Entity</div>
                    <div>Date</div>
                    <div>Score</div>
                    <div>Failed</div>
                    <div className="text-right">Actions</div>
                  </div>

                  {(index || []).slice(0, 25).map((r) => {
                    const isSelected = String(r?.snapshot_id) === String(selectedId);

                    return (
                      <div
                        key={String(r?.snapshot_id)}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedId(r.snapshot_id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") setSelectedId(r.snapshot_id);
                        }}
                        className={cn(
                          "grid grid-cols-7 px-4 py-3 text-sm border-b last:border-b-0 cursor-pointer",
                          isSelected ? "bg-slate-50" : "bg-white hover:bg-slate-50"
                        )}
                        title="Click to select this snapshot"
                      >
                        <div className="col-span-2 font-bold">{r.entity_name || "Not specified"}</div>
                        <div className="text-slate-600">{fmtDateTime(r.created_at_iso)}</div>
                        <div className="font-black">{fmtPct(r.compliance_score_pct)}</div>
                        <div className="font-black text-red-700">{Number(r.failed_count) || 0}</div>

                        {/* Actions: Go to Remediation + View */}
                        <div className="text-right flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedId(r.snapshot_id);
                              const p = safeGetSnapshotPayload(r.snapshot_id);
                              const ctx = buildRemediationContext({ snapshotId: r.snapshot_id, payload: p, meta: r });
                              safeWriteRemediationContext(ctx);
                              navigate("/remediation");
                            }}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-800 hover:bg-slate-50"
                            title="Go to Remediation for this snapshot"
                          >
                            Go to Remediation
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/audit-snapshot/${encodeURIComponent(String(r.snapshot_id))}`);
                            }}
                            className="rounded-xl bg-slate-900 px-3 py-2 text-xs font-black text-white hover:bg-slate-800"
                          >
                            View →
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {!index?.length && <div className="p-4 text-sm text-slate-600">No stored snapshots yet.</div>}
                </div>
              </div>

              {/* Footer */}
              <div className="mt-10 pb-8 text-xs text-slate-400">
                © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}